import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export type PageSelectionV1 = {
    screenerOptimizerJobId?: number;
};

export type PageSelectionUpdateV1 = {
    screenerOptimizerJobId?: number | null;
};

export type PageContextV1 = {
    version: 1;
    routeKey: string;
    route: {
        pathname: string;
        search: string;
        hash: string;
    };
    params: {
        experimentId?: number;
        strategyId?: number;
        optionBuyingStrategyId?: number;
    };
    query: {
        strategyId?: number;
    };
    selection: PageSelectionV1;
};

type PageContextValue = {
    pageContext: PageContextV1;
    updateSelection: (patch: PageSelectionUpdateV1) => void;
    clearSelection: () => void;
};

type PageSelectionState = {
    pathname: string;
    selection: PageSelectionV1;
};

const PageContextContext = createContext<PageContextValue | null>(null);

const exactRouteKeys: { pathname: string; routeKey: string }[] = [
    { pathname: '/', routeKey: 'home' },
    { pathname: '/login', routeKey: 'login' },
    { pathname: '/register', routeKey: 'register' },
    { pathname: '/user/chat', routeKey: 'user_chat' },
    { pathname: '/user/chat/new', routeKey: 'user_chat_new' },
    { pathname: '/user/chat/autopilot', routeKey: 'user_chat_autopilot' },
    { pathname: '/user/aero', routeKey: 'user_aero' },
    { pathname: '/user/aero/new', routeKey: 'user_aero_new' },
    { pathname: '/user/experiments', routeKey: 'user_experiments' },
    { pathname: '/user/experimentCompare', routeKey: 'user_experiment_compare' },
    { pathname: '/user/experiment/new', routeKey: 'user_experiment_new' },
    { pathname: '/user/ideaLab', routeKey: 'user_idea_lab' }
];

const routeKeyPatterns: { pattern: RegExp; routeKey: string }[] = [
    { pattern: /^\/user\/chat\/\d+$/, routeKey: 'user_chat_session' },
    { pattern: /^\/user\/experimentResults\/\d+\/entities$/, routeKey: 'user_experiment_entity_results' },
    { pattern: /^\/user\/experimentResults\/\d+$/, routeKey: 'user_experiment_results' },
    { pattern: /^\/user\/experiment\/\d+$/, routeKey: 'user_experiment_editor' },
    { pattern: /^\/user\/aero\/\d+$/, routeKey: 'user_aero_detail' },
    { pattern: /^\/user\/tradingStrategy\/\d+$/, routeKey: 'user_trading_strategy_editor' },
    { pattern: /^\/user\/optionBuyingStrategy\/\d+$/, routeKey: 'user_option_buying_strategy_editor' },
    { pattern: /^\/user\/strategyDashboard\/\d+$/, routeKey: 'user_strategy_dashboard' },
    { pattern: /^\/user\/ideaLab\/study\/\d+$/, routeKey: 'user_idea_lab_study' },
    { pattern: /^\/user\/ideaLab\/variant\/\d+$/, routeKey: 'user_idea_lab_variant' },
    { pattern: /^\/user\/ideaLab\/run\/\d+$/, routeKey: 'user_idea_lab_run' }
];

function parseOptionalPositiveInt(value: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return undefined;
    const floored = Math.floor(parsed);
    return floored > 0 ? floored : undefined;
}

function deriveRouteKey(pathname: string): string {
    const exactRouteKey = exactRouteKeys.find((candidate) => candidate.pathname === pathname);
    if (exactRouteKey) return exactRouteKey.routeKey;

    const matchedRoute = routeKeyPatterns.find(({ pattern }) => pattern.test(pathname));
    if (matchedRoute) return matchedRoute.routeKey;

    return 'unknown';
}

function extractExperimentId(pathname: string): number | undefined {
    const match = pathname.match(/^\/user\/experimentResults\/(\d+)(?:\/|$)/) ?? pathname.match(/^\/user\/experiment\/(\d+)(?:\/|$)/);

    if (!match) return undefined;
    return parseOptionalPositiveInt(match[1] ?? null);
}

function extractStrategyId(pathname: string): number | undefined {
    const match = pathname.match(/^\/user\/tradingStrategy\/(\d+)(?:\/|$)/) ?? pathname.match(/^\/user\/strategyDashboard\/(\d+)(?:\/|$)/);

    if (!match) return undefined;
    return parseOptionalPositiveInt(match[1] ?? null);
}

function extractOptionBuyingStrategyId(pathname: string): number | undefined {
    const match = pathname.match(/^\/user\/optionBuyingStrategy\/(\d+)(?:\/|$)/);
    if (!match) return undefined;
    return parseOptionalPositiveInt(match[1] ?? null);
}

export function usePageContext(): PageContextValue {
    const context = useContext(PageContextContext);
    if (!context) throw new Error('usePageContext must be used within a PageContextProvider');
    return context;
}

export function PageContextProvider({ children }: { children: ReactNode }) {
    const location = useLocation();
    const [selectionState, setSelectionState] = useState<PageSelectionState>({ pathname: '', selection: {} });
    const currentSelection = selectionState.pathname === location.pathname ? selectionState.selection : {};

    const updateSelection = useCallback(
        (patch: PageSelectionUpdateV1) => {
            setSelectionState((previousSelectionState) => {
                const previousSelection = previousSelectionState.pathname === location.pathname ? previousSelectionState.selection : {};
                const nextSelection: PageSelectionV1 = { ...previousSelection };

                if ('screenerOptimizerJobId' in patch) {
                    const value = patch.screenerOptimizerJobId;
                    if (value === null || value === undefined) delete nextSelection.screenerOptimizerJobId;
                    else nextSelection.screenerOptimizerJobId = value;
                }

                return { pathname: location.pathname, selection: nextSelection };
            });
        },
        [location.pathname]
    );

    const clearSelection = useCallback(() => {
        setSelectionState({ pathname: location.pathname, selection: {} });
    }, [location.pathname]);

    const pageContext = useMemo<PageContextV1>(() => {
        const searchParams = new URLSearchParams(location.search);

        return {
            version: 1,
            routeKey: deriveRouteKey(location.pathname),
            route: {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash
            },
            params: {
                experimentId: extractExperimentId(location.pathname),
                strategyId: extractStrategyId(location.pathname),
                optionBuyingStrategyId: extractOptionBuyingStrategyId(location.pathname)
            },
            query: {
                strategyId: parseOptionalPositiveInt(searchParams.get('strategyId'))
            },
            selection: currentSelection
        };
    }, [currentSelection, location.hash, location.pathname, location.search]);

    const value: PageContextValue = useMemo(
        () => ({
            pageContext,
            updateSelection,
            clearSelection
        }),
        [pageContext, updateSelection, clearSelection]
    );

    return <PageContextContext.Provider value={value}>{children}</PageContextContext.Provider>;
}
